import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { CarFront, Package, Plane } from 'lucide-react';

import { AuthGuard } from '../../app/AuthGuard';
import {
  customerCabinetApi,
  type FileAssetDto,
  type OfferDto,
  type TransportDto,
  type TransportRequestDto,
} from '../../features/customerCabinet/api';
import { carrierOfferLabel } from '../../features/customerCabinet/userLabels';
import {
  CustomerCabinetLayout,
  navigateCustomer,
  type CustomerCabinetScreenId,
  type CustomerFigmaNodes,
} from '../../layouts/CustomerCabinet/CustomerCabinetLayout';
import { useFigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { CabinetEmptyState, CabinetLoadState, ProfileLink, RequestCardUnified, Tabs, type TabsPosition } from '../../ui';
import { carrierBoxLabel, documentsStatusLabel, PetIcon } from '../CarrierCabinet/carrierCabinetShared';
import { useLanguage } from '../../i18n/LanguageProvider';
import { readStoredLanguage } from '../../i18n/locale';
import { getRuntimeTimezone } from '../../i18n/preferences';
import { openPhotoGallery } from '../../utils/photoGallery';
import { CustomerPageHeading, formatRatingScore, transportsCountLabel } from './customerCabinetShared';
import './customer-cabinet.css';

export type CustomerRequestScreenId = Extract<CustomerCabinetScreenId,
  'my-requests' | 'request-detail' | 'payment' | 'active-transport'
>;

const FIGMA_NODES: Record<CustomerRequestScreenId, CustomerFigmaNodes> = {
  'my-requests': { '1920': '124:31186', '1440': '137:36197', '768': '137:37100', '375': '137:39949' },
  'request-detail': { '1920': '2680:41013', '1440': '2680:40639', '768': '2616:17345', '375': '2616:17602' },
  payment: { '1920': '2680:39808', '1440': '2680:40547', '768': '2598:17345', '375': '2598:17469' },
  'active-transport': { '1920': '2680:39410', '1440': '2680:40059', '768': '2588:17345', '375': '2589:17345' },
};

const GEOMETRY: Record<CustomerRequestScreenId, {
  height: Record<'desktop' | 'tablet' | 'mobile', number>;
  offset: Record<'desktop' | 'tablet' | 'mobile', number>;
}> = {
  'my-requests': { height: { desktop: 977, tablet: 1040, mobile: 1403 }, offset: { desktop: 0, tablet: 0, mobile: 0 } },
  'request-detail': { height: { desktop: 2046, tablet: 1552, mobile: 1625 }, offset: { desktop: 0, tablet: 209.5, mobile: 178.5 } },
  payment: { height: { desktop: 977, tablet: 506, mobile: 530 }, offset: { desktop: 0, tablet: 20, mobile: 6 } },
  'active-transport': { height: { desktop: 1301, tablet: 1507, mobile: 1276 }, offset: { desktop: 0, tablet: 9.5, mobile: 26.5 } },
};

type RequestTab = 'all' | 'active' | 'transit' | 'completed' | 'cancelled';

function hashNumber(name: string): number | null {
  const query = window.location.hash.split('?')[1] ?? '';
  const value = Number(new URLSearchParams(query).get(name));
  return Number.isInteger(value) && value > 0 ? value : null;
}

function money(value: number | null | undefined, currency = 'RUB') {
  if (value == null) return '';
  return new Intl.NumberFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value / 100);
}

function shortDate(value: string | null | undefined) {
  if (!value) return '';
  const normalized = value.includes('T') ? value : `${value}T00:00:00`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    ...(value.includes('T') ? { timeZone: getRuntimeTimezone() } : {}),
  })
    .format(date);
}

function deliveryTime(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    hour: '2-digit', minute: '2-digit', timeZone: getRuntimeTimezone(),
  }).format(date);
}

function dateRange(request: TransportRequestDto) {
  const first = shortDate(request.pickup_not_before);
  const last = shortDate(request.delivery_not_after);
  return first && last ? `${first} — ${last}` : first || last;
}

function displayDateRange(request: TransportRequestDto) {
  if (!request.pickup_not_before || !request.delivery_not_after) return dateRange(request);
  const from = new Date(`${request.pickup_not_before}T00:00:00`);
  const to = new Date(`${request.delivery_not_after}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return dateRange(request);
  const month = new Intl.DateTimeFormat('ru-RU', { month: 'long' }).format(to);
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    return `${from.getDate()}–${to.getDate()} ${month}`;
  }
  return `${from.getDate()} ${new Intl.DateTimeFormat('ru-RU', { month: 'long' }).format(from)} – ${to.getDate()} ${month}`;
}

const PET_TYPES: Record<string, string> = {
  dog: 'Собака',
  cat: 'Кошка',
  rodent: 'Грызун',
  bird: 'Птица',
  farm: 'Сельскохозяйственное животное',
  other: 'Другое животное',
};

const PET_TYPES_EN: Record<string, string> = {
  dog: 'Dog',
  cat: 'Cat',
  rodent: 'Rodent',
  bird: 'Bird',
  farm: 'Farm animal',
  other: 'Other animal',
};

function petDetails(request: TransportRequestDto, language: 'ru' | 'en') {
  return request.pets.map((pet) => [
    language === 'en' ? PET_TYPES_EN[pet.pet_type] ?? pet.custom_type : PET_TYPES[pet.pet_type] ?? pet.custom_type,
    language === 'en' && pet.breed === 'Метис' ? 'Mixed breed' : pet.breed,
    pet.name ? `${language === 'en' ? 'pet name' : 'кличка'}: ${pet.name}` : '',
    pet.weight_kg ? `${Number(pet.weight_kg)} ${language === 'en' ? 'kg' : 'кг'}` : '',
    pet.count > 1 ? `${pet.count} ${language === 'en' ? 'pets' : 'питомца'}` : '',
  ].filter(Boolean).join(' · ')).join('; ');
}

function questionCountLabel(count: number, language: 'ru' | 'en') {
  if (language === 'en') return `Questions from carriers · ${count} ${count === 1 ? 'question' : 'questions'}`;
  const rem100 = count % 100;
  const rem10 = count % 10;
  const noun = rem100 >= 11 && rem100 <= 19 ? 'вопросов' : rem10 === 1 ? 'вопрос' : rem10 >= 2 && rem10 <= 4 ? 'вопроса' : 'вопросов';
  return `Перевозчики спрашивают · ${count} ${noun}`;
}

function offersCountLabel(count: number, language: 'ru' | 'en') {
  if (language === 'en') return `Carrier offers${count > 0 ? ` · ${count}` : ''}`;
  return `Отклики перевозчиков${count > 0 ? ` · ${count}` : ''}`;
}

function budget(request: TransportRequestDto) {
  const first = money(request.budget_min_minor, request.currency);
  const last = money(request.budget_max_minor, request.currency);
  return first && last ? `${first} — ${last}` : first || last;
}

function feedingLabel(value: string) {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'owner_food') return 'Корм с собой';
  if (normalized === 'carrier_food') return 'Кормит перевозчик';
  return normalized.includes('_') ? 'Не указано' : value.trim();
}

function contactLabel(name?: string | null, phone?: string | null) {
  const clean = (value?: string | null) => value?.trim().replace(/^[·•⋅]+\s*|\s*[·•⋅]+$/g, '') || '';
  return [clean(name), clean(phone)].filter(Boolean).join(' ');
}

function compactPetDetails(request: TransportRequestDto) {
  const pet = request.pets[0];
  if (!pet) return { type: 'other', label: '', weight: '' };
  return {
    type: pet.pet_type,
    label: pet.breed || pet.custom_type || PET_TYPES[pet.pet_type] || '',
    weight: pet.weight_kg ? `${Number(pet.weight_kg)} кг` : '',
  };
}

function serviceLabel(value?: string) {
  if (value === 'air_courier') return 'Авиа';
  if (value === 'cargo_broker') return 'Карго';
  if (value === 'airport_meet') return 'Встреча';
  return 'Авто';
}

function ServiceIcon({ value }: { value?: string }) {
  const props = { 'aria-hidden': true, size: 24, strokeWidth: 1.5 } as const;
  if (value === 'air_courier') return <Plane {...props} />;
  if (value === 'cargo_broker') return <Package {...props} />;
  return <CarFront {...props} />;
}

function statusLabel(status: string) {
  if (status === 'draft') return 'Черновик';
  if (status === 'published') return 'Открыта';
  if (status === 'accepted' || status === 'matched') return 'Перевозчик выбран';
  if (status === 'planned') return 'Запланирована';
  if (status === 'in_progress') return 'В пути';
  if (status === 'delivered') return 'Доставлена';
  if (status === 'completed' || status === 'closed') return 'Завершена';
  if (status === 'cancelled') return 'Отменена';
  if (status === 'disputed') return 'Спор';
  if (status === 'sent') return 'Отправлен';
  if (status === 'rejected' || status === 'not_selected') return 'Не выбран';
  return status;
}

function statusGroup(status: string): RequestTab {
  if (['in_progress'].includes(status)) return 'transit';
  if (['completed', 'closed', 'delivered'].includes(status)) return 'completed';
  if (status === 'cancelled') return 'cancelled';
  return 'active';
}

function requestCardTone(status: string) {
  if (['published', 'accepted', 'matched'].includes(status)) return 'green';
  if (status === 'in_progress') return 'yellow';
  return 'gray';
}

function PageLayout({ children, screen }: { children: ReactNode; screen: CustomerRequestScreenId }) {
  const geometry = GEOMETRY[screen];
  return (
    <CustomerCabinetLayout
      contentHeight={geometry.height}
      figmaNode={FIGMA_NODES[screen]}
      screen={screen}
      topOffset={geometry.offset}
    >
      {children}
    </CustomerCabinetLayout>
  );
}

function MyRequestsView({ initialTab = 'all' }: { initialTab?: RequestTab }) {
  const breakpoint = useFigmaBreakpoint();
  const [requests, setRequests] = useState<TransportRequestDto[]>([]);
  const [offers, setOffers] = useState<OfferDto[]>([]);
  const [transports, setTransports] = useState<TransportDto[]>([]);
  const [tab, setTab] = useState<RequestTab>(initialTab);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    Promise.all([customerCabinetApi.myRequests(), customerCabinetApi.offers(), customerCabinetApi.transports()])
      .then(([nextRequests, nextOffers, nextTransports]) => {
        if (!active) return;
        setRequests(nextRequests);
        setOffers(nextOffers);
        setTransports(nextTransports);
        setState('ready');
      })
      .catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, []);

  const counts = useMemo(() => ({
    all: requests.length,
    active: requests.filter((item) => statusGroup(item.status) === 'active').length,
    transit: requests.filter((item) => statusGroup(item.status) === 'transit').length,
    completed: requests.filter((item) => statusGroup(item.status) === 'completed').length,
    cancelled: requests.filter((item) => statusGroup(item.status) === 'cancelled').length,
  }), [requests]);
  const visible = tab === 'all' ? requests : requests.filter((item) => statusGroup(item.status) === tab);
  const offersByRequest = useMemo(() => {
    const result = new Map<number, number>();
    offers.forEach((offer) => result.set(offer.request, (result.get(offer.request) ?? 0) + 1));
    return result;
  }, [offers]);
  const offerDetailsByRequest = useMemo(() => {
    const result = new Map<number, OfferDto[]>();
    offers.forEach((offer) => result.set(offer.request, [...(result.get(offer.request) ?? []), offer]));
    return result;
  }, [offers]);
  const transportsByRequest = useMemo(
    () => new Map(transports.map((transport) => [transport.request_id, transport])),
    [transports],
  );
  const newCount = requests.filter((request) => request.status === 'published').length;

  return (
    <PageLayout screen="my-requests">
      <div className="customer-requests" data-state={state}>
        <CustomerPageHeading
          className="customer-requests__top"
          figmaNode="137:36204"
          title={
            <span className="customer-requests__title">
              Мои заявки
              {newCount > 0 ? <span className="customer-requests__badge">{newCount} новые</span> : null}
            </span>
          }
          subtitle="Все ваши заявки на перевозку"
          action={
            <button className="customer-primary customer-requests__create-top" type="button" onClick={() => { window.location.hash = 'new-request-step-1'; }}>+ Добавить заявку</button>
          }
        />

        <Tabs
          active={({ all: 1, active: 2, transit: 3, completed: 4, cancelled: 5 } as const)[tab]}
          aria-label="Фильтр заявок"
          className="customer-request-tabs"
          onChange={(position: TabsPosition) => setTab((['all', 'active', 'transit', 'completed', 'cancelled'] as const)[position - 1])}
          size={breakpoint === '375' ? '375' : breakpoint === '768' ? '768' : '1920+1440'}
          slots={[
            { label: 'Все', badge: counts.all },
            { label: 'Активные', badge: counts.active },
            { label: 'В пути', badge: counts.transit },
            { label: 'Завершённые', badge: counts.completed },
            { label: 'Отменённые', badge: counts.cancelled },
          ]}
        />

        <div className="customer-request-list" data-figma-node="137:38797">
          <CabinetLoadState state={state} />
          {visible.map((request) => {
            const requestOffers = offerDetailsByRequest.get(request.id) ?? [];
            const selectedOffer = requestOffers.find((offer) => ['accepted', 'selected'].includes(offer.status));
            const transport = transportsByRequest.get(request.id);
            const effectiveStatus = transport?.status ?? request.status;
            const pet = compactPetDetails(request);
            const carrierService = selectedOffer?.carrier_service_type ?? requestOffers[0]?.carrier_service_type;

            return (
              <RequestCardUnified
                key={request.id}
                continuousRouteUnderline
                fromCity={request.from_city}
                toCity={request.to_city}
                requestCode={request.public_code ?? request.id}
                tone={requestCardTone(effectiveStatus) as 'green' | 'yellow' | 'gray' | 'default'}
                petType={pet.type}
                petLabel={pet.label}
                petWeight={pet.weight}
                dateRange={displayDateRange(request)}
                serviceLabel={serviceLabel(carrierService)}
                serviceIcon={<ServiceIcon value={carrierService} />}
                budget={budget(request)}
                offersCount={offersByRequest.get(request.id) ?? 0}
                selectedOfferText={
                  selectedOffer
                    ? carrierOfferLabel(selectedOffer.carrier_name, selectedOffer.carrier_is_deleted)
                    : undefined
                }
                rightState={
                  <>
                    <span className={`customer-status is-${requestCardTone(effectiveStatus)}`}>
                      {statusLabel(effectiveStatus)}
                    </span>
                    <small>#{request.public_code ?? request.id}</small>
                  </>
                }
                onClick={() => navigateCustomer('request-detail', { id: request.id })}
              />
            );
          })}
          {state === 'ready' && visible.length === 0 ? <CabinetEmptyState kind="requests" filtered={requests.length > 0} /> : null}
        </div>

        <button className="customer-primary customer-requests__create-mobile" type="button" onClick={() => { window.location.hash = 'new-request-step-1'; }}>+ Добавить заявку</button>
      </div>
    </PageLayout>
  );
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  if (value === '' || value == null) return null;
  return <div className="customer-detail-field"><span>{label}</span><strong>{value}</strong></div>;
}

function openPhotosGallery(photos: TransportRequestDto['photos'], initialIndex: number) {
  openPhotoGallery(photos.map((photo, index) => ({
    url: photo.file.url,
    alt: photo.file.original_filename || `Фото питомца ${index + 1}`,
  })), initialIndex);
}

function CustomerCheckpointMedia({ files, title }: { files: FileAssetDto[]; title: string }) {
  const photos = files.filter((file) => file.media_kind !== 'video' && !file.content_type?.startsWith('video/'));
  const gallery = photos.map((file, index) => ({
    url: file.url,
    alt: file.original_filename || `${title}, фото ${index + 1}`,
  }));

  return files.map((file) => {
    if (file.media_kind === 'video' || file.content_type?.startsWith('video/')) {
      return <video controls key={file.id} preload="metadata" src={file.url} />;
    }

    const photoIndex = photos.findIndex((photo) => photo.id === file.id);
    const alt = file.original_filename || `${title}, фото ${photoIndex + 1}`;
    return (
      <button
        aria-label={`Открыть ${alt} на весь экран`}
        key={file.id}
        type="button"
        onClick={() => openPhotoGallery(gallery, photoIndex)}
      >
        <img src={file.url} alt={alt} />
      </button>
    );
  });
}

interface RequestEditDraft {
  fromCity: string;
  toCity: string;
  pickupDate: string;
  deliveryDate: string;
  budgetMin: string;
  budgetMax: string;
  petNotes: string;
}

function editDraftFromRequest(request: TransportRequestDto): RequestEditDraft {
  return {
    fromCity: request.from_city,
    toCity: request.to_city,
    pickupDate: request.pickup_not_before ?? '',
    deliveryDate: request.delivery_not_after ?? '',
    budgetMin: request.budget_min_minor == null ? '' : String(request.budget_min_minor / 100),
    budgetMax: request.budget_max_minor == null ? '' : String(request.budget_max_minor / 100),
    petNotes: request.pet_notes,
  };
}

function RequestDetailView() {
  const { language } = useLanguage();
  const [request, setRequest] = useState<TransportRequestDto | null>(null);
  const [transport, setTransport] = useState<TransportDto | null>(null);
  const [hasReview, setHasReview] = useState(false);
  const [offers, setOffers] = useState<OfferDto[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [editDraft, setEditDraft] = useState<RequestEditDraft | null>(null);
  const [editError, setEditError] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'submitting' | 'error'>('loading');

  async function load() {
    const requestedId = hashNumber('id');
    const allRequests = requestedId ? null : await customerCabinetApi.myRequests();
    const id = requestedId ?? allRequests?.[0]?.id;
    if (!id) {
      setState('ready');
      return;
    }
    const [nextRequest, allOffers, allTransports, allReviews] = await Promise.all([
      customerCabinetApi.request(id),
      customerCabinetApi.offers(),
      customerCabinetApi.transports(),
      customerCabinetApi.reviews(),
    ]);
    setRequest(nextRequest);
    setOffers(allOffers.filter((offer) => offer.request === id));
    const matchedTransport = allTransports.find((t) => t.request_id === id) ?? null;
    setTransport(matchedTransport);
    if (matchedTransport) {
      setHasReview(allReviews.some((r) => r.transport === matchedTransport.id && r.author_role === 'customer'));
    }
    setState('ready');
  }

  useEffect(() => { load().catch(() => setState('error')); }, []);

  useEffect(() => {
    if (state !== 'ready' || new URLSearchParams(window.location.hash.split('?')[1]).get('section') !== 'questions') return;
    const frame = window.requestAnimationFrame(() => document.getElementById('request-questions')?.scrollIntoView({ block: 'start' }));
    return () => window.cancelAnimationFrame(frame);
  }, [state, request?.id]);

  async function answer(questionId: number) {
    if (!request || !answers[questionId]?.trim()) return;
    setState('submitting');
    try {
      await customerCabinetApi.answerQuestion(request.id, questionId, answers[questionId].trim());
      await load();
    } catch {
      setState('error');
    }
  }

  async function openChat(offerId: number) {
    setState('submitting');
    try {
      const chat = await customerCabinetApi.startOfferChat(offerId);
      navigateCustomer('chat', { chat: chat.id });
    } catch {
      setState('error');
    }
  }

  async function cancelRequest() {
    if (!request || !['draft', 'published'].includes(request.status)) return;
    const confirmed = window.confirm('Отменить заявку? Она исчезнет из ленты перевозчиков, а активные отклики будут закрыты.');
    if (!confirmed) return;
    setState('submitting');
    try {
      await customerCabinetApi.cancelRequest(request.id);
      navigateCustomer('my-requests');
    } catch {
      setState('error');
      window.alert('Не удалось отменить заявку. Обновите страницу и попробуйте ещё раз.');
    }
  }

  async function saveEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!request || !editDraft) return;
    const minimum = editDraft.budgetMin.trim() ? Number(editDraft.budgetMin.replace(',', '.')) : null;
    const maximum = editDraft.budgetMax.trim() ? Number(editDraft.budgetMax.replace(',', '.')) : null;
    if ([minimum, maximum].some((value) => value !== null && (!Number.isFinite(value) || value < 0))) {
      setEditError('Укажите корректную цену.');
      return;
    }
    if (minimum !== null && maximum !== null && minimum > maximum) {
      setEditError('Максимальная цена не может быть меньше минимальной.');
      return;
    }
    if (editDraft.pickupDate && editDraft.deliveryDate && editDraft.pickupDate > editDraft.deliveryDate) {
      setEditError('Доставка не может быть раньше забора питомца.');
      return;
    }
    setState('submitting');
    setEditError('');
    try {
      const updated = await customerCabinetApi.updateRequest(request.id, {
        from_city: editDraft.fromCity.trim(),
        to_city: editDraft.toCity.trim(),
        pickup_not_before: editDraft.pickupDate || null,
        delivery_not_after: editDraft.deliveryDate || null,
        budget_min_minor: minimum === null ? null : Math.round(minimum * 100),
        budget_max_minor: maximum === null ? null : Math.round(maximum * 100),
        pet_notes: editDraft.petNotes.trim(),
      });
      setRequest(updated);
      setEditDraft(null);
      setState('ready');
    } catch (error) {
      setEditError(error instanceof Error ? error.message : 'Не удалось сохранить заявку.');
      setState('ready');
    }
  }

  return (
    <PageLayout screen="request-detail">
      <div className="customer-detail" data-state={state}>
        <button className="customer-outline customer-detail__back" type="button" onClick={() => navigateCustomer('my-requests')}>К списку заявок</button>
        {request ? (
          <>
            <CustomerPageHeading
              className="customer-detail__heading"
              figmaNode="2680:40643"
              title={<><span className="customer-detail__route">{request.from_city} → {request.to_city}</span><span className={`customer-status is-${requestCardTone(request.status)}`}>{statusLabel(request.status)}</span></>}
              subtitle={`Заявка #${request.public_code ?? request.id}`}
              action={
                <div className="customer-detail__heading-actions">
                  {['draft', 'published'].includes(request.status) ? (
                    <div className="customer-detail__request-actions">
                      {request.status === 'published' ? (
                        <button className="customer-outline" disabled={state === 'submitting'} type="button" onClick={() => { setEditDraft(editDraft ? null : editDraftFromRequest(request)); setEditError(''); }}>
                          {editDraft ? 'Закрыть' : 'Редактировать'}
                        </button>
                      ) : null}
                      <button className="customer-outline" disabled={state === 'submitting'} type="button" onClick={() => void cancelRequest()}>
                        {state === 'submitting' ? 'Отменяем…' : 'Отменить'}
                      </button>
                    </div>
                  ) : null}
                </div>
              }
            />

            <div className="customer-detail__content" data-figma-node="2680:40656">
              <section className="customer-panel customer-detail__parameters">
                <h2>Параметры перевозки</h2>
                {editDraft ? (
                  <form className="customer-detail-edit" onSubmit={(event) => void saveEdit(event)}>
                    <label>Откуда<input required value={editDraft.fromCity} onChange={(event) => setEditDraft({ ...editDraft, fromCity: event.target.value })} /></label>
                    <label>Куда<input required value={editDraft.toCity} onChange={(event) => setEditDraft({ ...editDraft, toCity: event.target.value })} /></label>
                    <label>Забор с<input type="date" value={editDraft.pickupDate} onChange={(event) => setEditDraft({ ...editDraft, pickupDate: event.target.value })} /></label>
                    <label>Доставка до<input type="date" value={editDraft.deliveryDate} onChange={(event) => setEditDraft({ ...editDraft, deliveryDate: event.target.value })} /></label>
                    <label>Цена от, {request.currency}<input type="number" min="0" step="0.01" inputMode="decimal" value={editDraft.budgetMin} onChange={(event) => setEditDraft({ ...editDraft, budgetMin: event.target.value })} /></label>
                    <label>Цена до, {request.currency}<input type="number" min="0" step="0.01" inputMode="decimal" value={editDraft.budgetMax} onChange={(event) => setEditDraft({ ...editDraft, budgetMax: event.target.value })} /></label>
                    <label className="customer-detail-edit__wide">Особенности питомца<textarea rows={3} value={editDraft.petNotes} onChange={(event) => setEditDraft({ ...editDraft, petNotes: event.target.value })} /></label>
                    {editError ? <p className="customer-detail-edit__error" role="alert">{editError}</p> : null}
                    <div className="customer-detail-edit__actions">
                      <button className="customer-outline" type="button" onClick={() => setEditDraft(null)}>Отмена</button>
                      <button className="customer-primary" disabled={state === 'submitting'} type="submit">{state === 'submitting' ? 'Сохраняем…' : 'Сохранить изменения'}</button>
                    </div>
                  </form>
                ) : null}
                <div className="customer-detail-grid">
                  <DetailField label="Питомец" value={petDetails(request, language)} />
                  <DetailField label="Сроки" value={`забор с ${shortDate(request.pickup_not_before)} · доставка до ${shortDate(request.delivery_not_after)}`} />
                  <DetailField label="Срочность" value={request.urgency === 'urgent' ? 'Срочная' : 'Обычная'} />
                  <DetailField label="Бюджет" value={budget(request)} />
                  <DetailField label="Переноска" value={carrierBoxLabel(request.carrier_box)} />
                  <DetailField label="Документы" value={documentsStatusLabel(request.documents_status)} />
                  <DetailField label="Особенности" value={request.pet_notes} />
                  <DetailField label="Кормление" value={feedingLabel(request.feeding_notes)} />
                  <DetailField label="Контакт при заборе" value={contactLabel(request.pickup_contact_name, request.pickup_contact_phone)} />
                  <DetailField label="Контакт при доставке" value={contactLabel(request.delivery_contact_name, request.delivery_contact_phone)} />
                </div>

                {request.photos.length ? (
                  <div className="customer-photo-grid">
                    {request.photos.map((photo, index) => (
                      <button
                        className="customer-photo-thumb"
                        key={photo.id}
                        type="button"
                        onClick={() => openPhotosGallery(request.photos, index)}
                        aria-label={`Открыть фото питомца ${index + 1}`}
                      >
                        <img src={photo.file.url} alt={photo.file.original_filename || `Фото питомца ${index + 1}`} />
                      </button>
                    ))}
                  </div>
                ) : null}
              </section>

              {['completed', 'closed'].includes(request.status) ? (
                <section className="customer-panel customer-action-card">
                  <h2>Перевозка завершена</h2>
                  <p>{language === 'en'
                    ? hasReview ? 'You have already reviewed the carrier.' : 'You can now review the carrier and rate the transport.'
                    : hasReview ? 'Вы уже оставили отзыв о перевозчике.' : 'Теперь вы можете оставить отзыв о перевозчике и оценить качество поездки.'}</p>
                  <button
                    className="customer-primary"
                    disabled={hasReview}
                    data-i18n-skip
                    type="button"
                    onClick={() => navigateCustomer('review', transport ? { id: transport.id } : { request: request.id })}
                  >
                    {hasReview ? (language === 'en' ? 'Review submitted' : 'Отзыв оставлен') : (language === 'en' ? 'Leave a review' : 'Оставить отзыв')}
                  </button>
                </section>
              ) : null}

              <section id="request-questions" className={`customer-panel customer-detail__questions${request.questions.length === 0 ? ' is-empty' : ''}`}>
                <h2>{request.questions.length > 0
                  ? questionCountLabel(request.questions.length, language)
                  : language === 'en' ? 'Questions from carriers' : 'Перевозчики спрашивают'}</h2>
                <p className="customer-detail__section-note">Перевозчики уточняют детали до отклика. Ответ увидят все перевозчики. Личные данные обсуждайте в приватном чате.</p>
                {request.questions.length > 0 ? (
                  <div className="customer-question-list">
                    {request.questions.map((question) => (
                      <div className="customer-question" key={question.id}>
                        <div className="customer-question__head">
                          <strong><ProfileLink profileId={question.carrier} role="carrier">{question.carrier_name}</ProfileLink></strong>
                          <span className={question.answer ? 'is-answered' : 'is-waiting'}>{question.answer ? 'Ваш ответ' : 'Ждёт ответа'}</span>
                        </div>
                        <p>{question.question}</p>
                        {question.answer ? <em><b>Ваш ответ</b>{question.answer}</em> : (
                          <div className="customer-question__answer-form">
                            <input value={answers[question.id] ?? ''} placeholder="Ваш ответ (увидят все перевозчики)…" onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))} />
                            <button className="customer-primary" disabled={state === 'submitting'} type="button" onClick={() => void answer(question.id)}>Ответить публично</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="customer-detail__empty-note">Вопросов от перевозчиков пока нет</p>
                )}
              </section>

              {!['completed', 'closed'].includes(request.status) ? (
                <section className={`customer-panel customer-detail__offers${offers.length === 0 ? ' is-empty' : ''}`}>
                  <h2>{offersCountLabel(offers.length, language)}</h2>
                  <p className="customer-detail__section-note">Цены специально скрыты. Выбирайте по портфолио, рейтингу и отзывам, а стоимость обсудите в приватном чате.</p>
                  {offers.length > 0 ? (
                    <div className="customer-offer-list">
                      {offers.map((offer) => (
                        <article className="customer-offer" key={offer.id}>
                          <div>
                            <h3
                              className="customer-offer__carrier-name"
                              role={offer.carrier ? 'button' : undefined}
                              tabIndex={offer.carrier ? 0 : undefined}
                              title="Открыть профиль перевозчика"
                              onClick={() => {
                                if (offer.carrier) {
                                  window.location.hash = `carrier-profile-external?id=${offer.carrier}`;
                                }
                              }}
                              onKeyDown={(e) => {
                                if ((e.key === 'Enter' || e.key === ' ') && offer.carrier) {
                                  e.preventDefault();
                                  window.location.hash = `carrier-profile-external?id=${offer.carrier}`;
                                }
                              }}
                            >
                              {offer.carrier_name}
                            </h3>
                            <p>
                              {language === 'en' ? 'Rating' : 'Рейтинг'} {formatRatingScore(offer.carrier_rating)}
                              <span className="customer-offer__dot">·</span>
                              {transportsCountLabel(offer.carrier_completed_transports_count, language)}
                            </p>
                            <p>{offer.comment}</p>
                          </div>
                          <strong className="customer-offer__price">Цена — в чате</strong>
                          <div className="customer-offer__actions">
                            <button className="customer-primary" type="button" onClick={() => navigateCustomer('payment', { offer: offer.id, request: request.id })}>Открыть</button>
                            <button className="customer-outline" disabled={state === 'submitting' || !['sent', 'accepted'].includes(offer.status)} type="button" onClick={() => void openChat(offer.id)}>Чат</button>
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="customer-detail__empty-note">Откликов пока нет</p>
                  )}
                </section>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </PageLayout>
  );
}

function PaymentView() {
  const [offer, setOffer] = useState<OfferDto | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'submitting' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    customerCabinetApi.offers()
      .then((offers) => {
        if (!active) return;
        const id = hashNumber('offer');
        setOffer(offers.find((item) => item.id === id) ?? offers[0] ?? null);
        setState('ready');
      })
      .catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, []);

  async function confirm() {
    if (!offer) return;
    setState('submitting');
    try {
      const result = await customerCabinetApi.acceptOffer(offer.id);
      navigateCustomer('active-transport', { id: result.transport_id });
    } catch {
      setState('error');
    }
  }

  return (
    <PageLayout screen="payment">
      <div className="customer-payment" data-state={state}>
        <CustomerPageHeading
          className="customer-payment__heading"
          figmaNode="2680:40551"
          title="Подтверждение перевозчика"
          subtitle="Проверьте условия отклика перед созданием сделки"
        />
        {offer ? (
          <section className="customer-panel customer-payment__card" data-figma-node="2680:40558">
            <h2>Вы выбираете <ProfileLink profileId={offer.carrier} role="carrier">{offer.carrier_name}</ProfileLink>?</h2>
            <p>После подтверждения будет создана сделка, откроется чат и начнётся подготовка к перевозке.</p>
            <div className="customer-payment__summary">
              <div><span>Стоимость перевозки</span><strong>{money(offer.total_price_minor, offer.currency)}</strong></div>
              <div><span>Дата выезда</span><strong>{shortDate(offer.departure_date)}</strong></div>
              <div><span>Дата доставки</span><strong>{shortDate(offer.delivery_date)}</strong></div>
            </div>
            <div className="customer-alert">Оплата выполняется напрямую перевозчику. Условия и способ оплаты зафиксируются в сделке.</div>
            <button className="customer-primary" disabled={state === 'submitting'} type="button" onClick={() => void confirm()}>Подтвердить выбор</button>
            <button className="customer-link" type="button" onClick={() => navigateCustomer('request-detail', { id: offer.request })}>Вернуться к откликам</button>
          </section>
        ) : null}
      </div>
    </PageLayout>
  );
}

const TRANSPORT_STEPS = ['Заявка принята', 'Перевозка запланирована', 'В пути', 'Доставлена', 'Завершена'];

function transportStep(status: string) {
  if (status === 'planned' || status === 'accepted') return 1;
  if (status === 'in_progress') return 2;
  if (status === 'delivered') return 3;
  if (status === 'closed' || status === 'completed') return 4;
  return 0;
}

function paymentLabel(status: string) {
  if (status === 'agreed_direct') return 'Согласована напрямую';
  if (status === 'customer_marked_paid') return 'Заказчик отметил оплату';
  if (status === 'carrier_confirmed') return 'Оплата подтверждена';
  return 'Не согласована';
}

function ActiveTransportView() {
  const { language } = useLanguage();
  const [transport, setTransport] = useState<TransportDto | null>(null);
  const [hasReview, setHasReview] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'submitting' | 'error'>('loading');

  async function load() {
    const [transports, reviews] = await Promise.all([customerCabinetApi.transports(), customerCabinetApi.reviews()]);
    const id = hashNumber('id');
    const next = transports.find((item) => item.id === id)
      ?? transports.find((item) => !['closed', 'cancelled'].includes(item.status))
      ?? transports[0]
      ?? null;
    setTransport(next);
    setHasReview(reviews.some((review) => review.transport === next?.id && review.author_role === 'customer'));
    setState('ready');
  }

  useEffect(() => { load().catch(() => setState('error')); }, []);

  useEffect(() => {
    if (transport?.payment_status !== 'customer_marked_paid') return undefined;
    const interval = window.setInterval(() => {
      load().catch(() => setState('error'));
    }, 10000);
    return () => window.clearInterval(interval);
  }, [transport?.payment_status]);

  async function paymentAction(action: 'agree' | 'paid') {
    if (!transport) return;
    setState('submitting');
    try {
      if (action === 'agree') await customerCabinetApi.agreeDirectPayment(transport.deal);
      else await customerCabinetApi.customerPaid(transport.deal);
      await load();
    } catch {
      setState('error');
    }
  }

  async function confirmReceipt() {
    if (!transport) return;
    setState('submitting');
    try {
      await customerCabinetApi.confirmReceipt(transport.id);
      await load();
    } catch {
      setState('error');
    }
  }

  const activeStep = transport ? transportStep(transport.status) : 0;

  return (
    <PageLayout screen="active-transport">
      <div className="customer-transport" data-state={state}>
        {transport ? (
          <>
            <CustomerPageHeading
              className="customer-transport__heading"
              figmaNode="2680:40195"
              title="Активная перевозка"
              subtitle={`${transport.from_city} → ${transport.to_city} · ${transport.request_code}`}
              action={
                <button className="customer-outline" type="button" onClick={() => navigateCustomer('chat', { transport: transport.id })}>Открыть чат</button>
              }
            />

            <div className="customer-transport__route" data-figma-node="2680:40062">
              {TRANSPORT_STEPS.map((label, index) => (
                <div className={index < activeStep ? 'is-done' : index === activeStep ? 'is-current' : ''} key={label}>
                  <i />
                  <strong>{label}</strong>
                  <span>{index < activeStep ? 'Выполнено' : index === activeStep ? 'Текущий этап' : 'Ожидается'}</span>
                </div>
              ))}
            </div>

            <div className="customer-transport__columns">
              <section className="customer-panel customer-transport__progress" data-figma-node="2680:40126">
                <h2>Ход перевозки</h2>
                <div className="customer-checkpoint-list">
                  {transport.checkpoints.map((checkpoint) => (
                    <article key={checkpoint.id}>
                      <i />
                      <div><strong>{checkpoint.title}</strong><p>{checkpoint.comment}</p></div>
                      <time>{shortDate(checkpoint.occurred_at ?? checkpoint.created_at.slice(0, 10))}</time>
                      {checkpoint.media_files.length ? (
                        <div className="customer-checkpoint-media"><CustomerCheckpointMedia files={checkpoint.media_files} title={checkpoint.title} /></div>
                      ) : null}
                    </article>
                  ))}
                </div>
              </section>

              <aside className="customer-transport__side" data-figma-node="2680:40085">
                {transport.status === 'delivered' && !transport.customer_received_at ? (
                  <section className="customer-action-card customer-action-card--receipt" data-i18n-skip>
                    <h2>{language === 'en' ? '✓ Is your pet with you? Please confirm' : '✓ Питомец у вас? Подтвердите'}</h2>
                    <p>
                      {language === 'en'
                        ? `The carrier marked the delivery${transport.delivered_at ? ` at ${deliveryTime(transport.delivered_at)}` : ''}. If everything is fine, click the button. If there is a problem, select “Something is wrong”.`
                        : `Перевозчик отметил доставку${transport.delivered_at ? ` в ${deliveryTime(transport.delivered_at)}` : ''}. Если всё хорошо — нажмите кнопку. Если есть проблема — выберите «Что-то не так».`}
                    </p>
                    <button className="customer-primary" disabled={state === 'submitting'} type="button" onClick={() => void confirmReceipt()}>{language === 'en' ? '✓ I received my pet' : '✓ Я получил питомца'}</button>
                    <button className="customer-outline" type="button" onClick={() => navigateCustomer('dispute', { deal: transport.deal })}>{language === 'en' ? 'Something is wrong' : 'Что-то не так'}</button>
                  </section>
                ) : null}
                <section className="customer-action-card">
                  <h2>Расчёты по сделке</h2>
                  <p>{money(transport.total_price_minor, transport.currency)}<br />{paymentLabel(transport.payment_status)}</p>
                  {transport.payment_status === 'not_agreed' ? <button className="customer-outline" disabled={state === 'submitting'} type="button" onClick={() => void paymentAction('agree')}>Согласовать оплату</button> : null}
                  {transport.payment_status === 'agreed_direct' ? <button className="customer-primary" disabled={state === 'submitting'} type="button" onClick={() => void paymentAction('paid')}>Я оплатил</button> : null}
                  {transport.payment_status === 'customer_marked_paid' ? <div className="customer-payment-state" data-i18n-skip><strong>{language === 'en' ? '✓ Payment marked' : '✓ Оплата отмечена'}</strong><span>{language === 'en' ? 'Awaiting carrier confirmation' : 'Ожидаем подтверждения перевозчика'}</span></div> : null}
                  {transport.payment_status === 'carrier_confirmed' ? <div className="customer-payment-state is-confirmed"><strong>✓ Оплата подтверждена</strong><span>Перевозчик подтвердил получение денег</span></div> : null}
                </section>
                <section className="customer-action-card"><h2>Перевозчик</h2><p><ProfileLink profileId={transport.carrier_id} role="carrier">{transport.carrier_name}</ProfileLink></p><button className="customer-outline" type="button" onClick={() => navigateCustomer('chat', { transport: transport.id })}>Написать в чат</button></section>
                {transport.customer_received_at || transport.status === 'closed' ? <section className="customer-action-card" data-i18n-skip>
                  <h2>{language === 'en' ? 'Transport completed' : 'Перевозка завершена'}</h2>
                  <p>{hasReview ? (language === 'en' ? 'Thank you for your review.' : 'Спасибо за ваш отзыв.') : (language === 'en' ? 'You can now leave a review of the carrier.' : 'Теперь вы можете оставить отзыв о перевозчике.')}</p>
                  <button className="customer-primary" disabled={hasReview || state !== 'ready'} type="button" onClick={() => navigateCustomer('review', { id: transport.id })}>{hasReview ? (language === 'en' ? 'Review submitted' : 'Отзыв оставлен') : (language === 'en' ? 'Leave a review' : 'Оставить отзыв')}</button>
                  <button className="customer-link" type="button" onClick={() => navigateCustomer('dispute', { deal: transport.deal })}>{language === 'en' ? 'Report a problem' : 'Сообщить о проблеме'}</button>
                </section> : null}
              </aside>
            </div>
          </>
        ) : (
          <div className="customer-transport__empty">
            <CustomerPageHeading
              figmaNode="2680:40195"
              title="Активные перевозки"
            />
            <CabinetLoadState state={state} />
            {state === 'ready' ? <><CabinetEmptyState kind="transports" />
            <button
              className="customer-primary"
              type="button"
              onClick={() => navigateCustomer('my-requests')}
            >
              Мои заявки
            </button>
            </> : null}
          </div>
        )}
      </div>
    </PageLayout>
  );
}

export function CustomerRequestsScreen({ screen }: { screen: CustomerRequestScreenId }) {
  return (
    <AuthGuard allow={['customer']}>
      {screen === 'my-requests' ? <MyRequestsView initialTab="all" /> : null}
      {screen === 'request-detail' ? <RequestDetailView /> : null}
      {screen === 'payment' ? <PaymentView /> : null}
      {screen === 'active-transport' ? <ActiveTransportView /> : null}
    </AuthGuard>
  );
}
